import { get, isEqual, isObject } from "lodash";
import { DateFormatOptions, DateTimeFormatOptions, TimeFormatOptions } from "./dateFormat";
import { ListFormatOptions } from "./localeFormat";
import { TMessageKey } from "./types.gen";
import { DateValue } from "../utils/Dates";

type AmountParam = { type: "amount"; value: number; currency?: string };
type Capitalize = { type: "capitalize"; value: string | TMessage | TMessageParam };
type CountryParam = { type: "country"; code: string };
type DateParam = { type: "date"; value: string | number | Date | undefined } & DateFormatOptions;
type DateTimeParam = { type: "datetime"; value: string | number | Date } & DateTimeFormatOptions;
type ExactAmountParam = { type: "exactAmount"; value: number; currency: string };
type ListParam = { type: "list"; value: (TMessageParam | string | number)[] } & ListFormatOptions;
type MdList = { type: "md-list"; value: (TMessage | TMessageParam | string | number)[] };
type NominalValueParam = { type: "nominal-value"; value: number; currency: string };
type NumberParam = { type: "number"; value: number; maximumFactionDigits?: number };
type PercentParam = { type: "percent"; value: number };
type PriceParam = { type: "price"; value: number; currency: string };
type SsnParam = { type: "ssn"; value: string; options?: { nationality?: string } };
type TimeParam = { type: "time"; value: string | number | Date | undefined } & TimeFormatOptions;
type Uncapitilize = { type: "uncapitilize"; value: string | TMessage | TMessageParam };
type WeekdayParam = { type: "weekday"; value: string | number | Date };
type YearParam = { type: "year"; value: DateValue };
type YearMonthParam = { type: "year-month"; value: DateValue };

export type TMessageParam =
  | AmountParam
  | Capitalize
  | CountryParam
  | DateParam
  | DateTimeParam
  | ExactAmountParam
  | ListParam
  | MdList
  | NominalValueParam
  | NumberParam
  | PercentParam
  | PriceParam
  | SsnParam
  | TimeParam
  | Uncapitilize
  | WeekdayParam
  | YearParam
  | YearMonthParam;

const paramTypes: Record<TMessageParam["type"], true> = {
  amount: true,
  capitalize: true,
  country: true,
  date: true,
  datetime: true,
  exactAmount: true,
  list: true,
  "md-list": true,
  number: true,
  "nominal-value": true,
  percent: true,
  price: true,
  ssn: true,
  time: true,
  uncapitilize: true,
  weekday: true,
  year: true,
  "year-month": true,
};

// @ts-expect-error n/a
export const isTMessageParam = (o: unknown): o is TMessageParam => paramTypes[get(o, "type")];

export type TMessageParams = {
  [key: string]:
    | TMessageParam
    | TMessage
    | number
    | string
    | null
    | undefined
    | Array<TMessageParam | TMessage | number | string | null | undefined>;
};

export type TMessage = { key: TMessageKey; params?: TMessageParams } | { translated: string };

export type TFunction = (
  keyOrMsg: TMessageKey | TMessage | TMessageParam,
  params?: TMessageParams,
) => string;

export function isTranslatedMessage(o: unknown): o is { translated: string } {
  return isObject(o) && "translated" in o && typeof o.translated === "string";
}

export function isTMessage(o: unknown): o is TMessage {
  if (o === null || o === undefined || typeof o !== "object") {
    return false;
  }
  const keys = Object.keys(o).sort();
  return (
    isEqual(keys, ["translated"]) || isEqual(keys, ["key"]) || isEqual(keys, ["key", "params"])
  );
}

export const brregKey = (brMessage: string): TMessageKey => `brreg.${brMessage}` as TMessageKey;
